
import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { format, addDays, isBefore } from 'date-fns';
import { Bell, BellRing, Calendar, ChevronDown, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';

export function ComplianceNotificationSystem() {
  const { toast } = useToast();
  const [unreadCount, setUnreadCount] = useState<number>(0);
  
  // Fetch upcoming deadlines and notifications
  const { data: notifications, isLoading } = useQuery({
    queryKey: ['compliance-notifications'],
    queryFn: async () => {
      // In a production app, this would fetch from a notifications table
      // For now, we'll generate some static notifications based on current date
      
      const today = new Date();
      const nextWeek = addDays(today, 7);
      const nextMonth = addDays(today, 30);
      
      // Get certificate reports due soon
      const { data: reports, error: reportsError } = await supabase
        .from('certificate_reports')
        .select('*')
        .gt('due_date', today.toISOString())
        .lt('due_date', nextMonth.toISOString())
        .order('due_date', { ascending: true });
      
      if (reportsError) throw reportsError;
      
      // Generate notifications from the reports
      const reportNotifications = (reports || []).map(report => {
        // Check if the due_date exists, if not use generated_at + 30 days as fallback
        const dueDate = report.due_date 
          ? new Date(report.due_date) 
          : addDays(new Date(report.generated_at), 30);
        
        return {
          id: `report-${report.id}`,
          type: 'deadline',
          title: `${report.state} Report Due Soon`,
          message: `Quarterly report for ${report.state} is due on ${format(dueDate, 'MMM d, yyyy')}`,
          date: dueDate.toISOString(),
          priority: isBefore(dueDate, nextWeek) ? 'high' : 'medium',
          read: report.status !== 'pending',
          actionUrl: `/admin/certificates/reports?reportId=${report.id}`
        };
      });
      
      // Add some static compliance notifications
      const staticNotifications = [
        {
          id: 'retention-1',
          type: 'retention',
          title: 'Retention Processing Due',
          message: 'Monthly retention processing is due in 3 days',
          date: addDays(today, 3).toISOString(),
          priority: 'medium',
          read: false,
          actionUrl: '/admin/certificates/retention'
        },
        {
          id: 'compliance-update-1',
          type: 'update',
          title: 'Florida Regulations Updated',
          message: 'Florida has updated their certificate reporting requirements',
          date: addDays(today, -2).toISOString(),
          priority: 'high',
          read: false,
          actionUrl: '/admin/certificates/reports'
        }
      ];
      
      // Combine all notifications
      const allNotifications = [...reportNotifications, ...staticNotifications];
      
      // Sort by date (newest first)
      allNotifications.sort((a, b) => {
        return new Date(a.date).getTime() - new Date(b.date).getTime();
      });
      
      return allNotifications;
    },
    refetchInterval: 60000, // Refetch every minute
  });
  
  // Update unread count when notifications change
  useEffect(() => {
    if (notifications) {
      const unread = notifications.filter(n => !n.read).length;
      setUnreadCount(unread);
    }
  }, [notifications]);
  
  const handleMarkAllAsRead = () => {
    // In a real app, this would update the database
    // For now, we'll just update the local state
    setUnreadCount(0);
    toast({
      title: "Notifications marked as read",
      description: "All notifications have been marked as read"
    });
  };
  
  const handleNotificationClick = (notification: any) => {
    // In a real app, this would mark the notification as read in the database
    // For now, we'll just navigate to the action URL if provided
    if (notification.actionUrl) {
      // This would typically use a router, but for simplicity:
      window.location.href = notification.actionUrl;
    }
  };
  
  const renderNotificationIcon = (type: string) => {
    switch (type) {
      case 'deadline':
        return <Calendar className="h-4 w-4 text-amber-500" />;
      case 'retention':
        return <Bell className="h-4 w-4 text-blue-500" />;
      case 'update':
        return <BellRing className="h-4 w-4 text-red-500" />;
      default:
        return <Bell className="h-4 w-4" />;
    }
  };
  
  return (
    <div>
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="icon" className="relative">
            <Bell className="h-[1.2rem] w-[1.2rem]" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 text-[10px] flex items-center justify-center">
                {unreadCount}
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-80 p-0">
          <div className="flex items-center justify-between p-2 border-b">
            <h4 className="font-medium text-sm">Compliance Notifications</h4>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-6 w-6">
                  <Settings className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Notification Settings</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleMarkAllAsRead}>
                  Mark all as read
                </DropdownMenuItem>
                <DropdownMenuItem>
                  Configure notifications
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          
          <div className="max-h-[300px] overflow-y-auto py-1">
            {isLoading ? (
              <div className="p-4 text-center text-sm text-muted-foreground">
                Loading notifications...
              </div>
            ) : notifications && notifications.length > 0 ? (
              notifications.map(notification => (
                <div
                  key={notification.id}
                  className={`p-2 hover:bg-muted cursor-pointer border-b last:border-0 ${
                    !notification.read ? 'bg-muted/50' : ''
                  }`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="flex items-start gap-2">
                    <div className="mt-0.5">
                      {renderNotificationIcon(notification.type)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm">{notification.title}</span>
                        {notification.priority === 'high' && (
                          <Badge variant="destructive" className="text-[10px] h-5">
                            Urgent
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {notification.message}
                      </p>
                      <div className="text-[10px] text-muted-foreground mt-1">
                        {format(new Date(notification.date), 'MMM d, yyyy')}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="p-4 text-center text-sm text-muted-foreground">
                No new notifications
              </div>
            )}
          </div>
          
          <div className="p-2 border-t text-center">
            <Button 
              variant="ghost" 
              size="sm" 
              className="w-full text-xs"
              onClick={() => {/* View all would navigate to a notifications page */}}
            >
              View all notifications
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
